/**
 * Sign In Prompt Component
 *
 * Displayed when user is not authenticated
 */

import { useAuth } from './AuthProvider';
import compassSvg from '../../assets/compass.svg?url';
import compassDarkSvg from '../../assets/compass-dark.svg?url';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, X } from 'lucide-react';

export function SignInPrompt() {
  const { signIn, authError, clearAuthError } = useAuth();

  return (
    <div className="relative flex h-screen h-[100dvh] w-full flex-col bg-background overflow-y-auto pt-[env(safe-area-inset-top)]">
      <div className="relative flex h-full w-full flex-col items-center justify-center p-4">
        <Card className="w-full max-w-sm border-0 shadow-none bg-transparent">
          <CardContent className="flex flex-col items-center justify-between h-full max-h-[calc(100dvh-2rem)] py-8 px-0">
            {/* Top Section: Logo and Text */}
            <div className="flex flex-col items-center flex-shrink-0">
              {/* App Logo */}
              <div className="flex items-center justify-center mb-8 px-4">
                <svg
                  width="240"
                  height="28"
                  viewBox="0 0 1871 219"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-full max-w-[240px] h-auto"
                >
                  <path
                    d="M0.507812 218.875V0.158936H49.9567L136.809 143.751V0.158936H182.454V218.875H133.006L46.153 75.2833V218.875H0.507812Z"
                    fill="currentColor"
                  />
                  <path
                    d="M226.604 165.623V53.4116C226.604 17.9098 244.355 0.158936 279.857 0.158936H352.128C387.63 0.158936 405.381 17.9098 405.381 53.4116V165.623C405.381 201.124 387.63 218.875 352.128 218.875H279.857C244.355 218.875 226.604 201.124 226.604 165.623ZM272.249 158.015C272.249 171.117 278.8 177.668 291.902 177.668H340.083C353.185 177.668 359.736 171.117 359.736 158.015V61.0191C359.736 47.9173 353.185 41.3664 340.083 41.3664H291.902C278.8 41.3664 272.249 47.9173 272.249 61.0191V158.015Z"
                    fill="currentColor"
                  />
                  <path
                    d="M554.047 101.593C560.387 101.593 563.556 98.4228 563.556 92.0832V50.8758C563.556 44.5362 560.387 41.3664 554.047 41.3664H495.089V218.875H449.443V0.158936H555.949C591.451 0.158936 609.201 17.9098 609.201 53.4116V89.5474C609.201 115.962 599.375 132.445 579.722 138.996L620.296 218.875H570.847L511.255 101.593H554.047Z"
                    fill="currentColor"
                  />
                  <path
                    d="M631.846 41.3664V0.158936H810.623V41.3664H744.057V218.875H698.412V41.3664H631.846Z"
                    fill="currentColor"
                  />
                  <path
                    d="M839.564 218.875V0.158936H885.209V85.7436H966.356V0.158936H1012V218.875H966.356V126.951H885.209V218.875H839.564Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1059.23 218.875V0.158936H1162.57C1198.07 0.158936 1215.82 17.9098 1215.82 53.4116V72.4304C1215.82 79.6153 1213.71 85.7436 1209.48 90.8153L1198.39 104.128L1211.7 115.223C1218.68 120.928 1222.16 129.381 1222.16 140.581V165.623C1222.16 201.124 1204.41 218.875 1168.91 218.875H1059.23ZM1104.88 177.668H1161.94C1171.66 177.668 1176.52 172.808 1176.52 163.087V141.532C1176.52 131.811 1171.66 126.951 1161.94 126.951H1104.88V177.668ZM1104.88 85.7436H1155.6C1165.32 85.7436 1170.18 80.8832 1170.18 71.1625V55.9475C1170.18 46.2267 1165.32 41.3664 1155.6 41.3664H1104.88V85.7436Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1259.36 165.623V0.158936H1305V158.015C1305 171.117 1311.55 177.668 1324.66 177.668H1366.5C1379.6 177.668 1386.15 171.117 1386.15 158.015V0.158936H1431.79V165.623C1431.79 201.124 1414.04 218.875 1378.54 218.875H1312.61C1277.11 218.875 1259.36 201.124 1259.36 165.623Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1477.21 218.875V0.158936H1522.86V218.875H1477.21Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1569.97 218.875V0.158936H1615.61V177.668H1710.07V218.875H1569.97Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1692.01 41.3664V0.158936H1870.79V41.3664H1804.22V218.875H1758.57V41.3664H1692.01Z"
                    fill="currentColor"
                  />
                </svg>
              </div>

              {/* Headline */}
              <h1 className="tracking-light text-[32px] font-semibold leading-tight text-center font-display">
                ASK COMPASS
              </h1>

              {/* Body Text */}
              <p className="text-muted-foreground text-base font-normal leading-normal pt-2 px-4 text-center font-display">
                Customer Intelligence Hub
              </p>
            </div>

            {/* Center Section: Image */}
            <div className="flex flex-col items-center justify-center flex-1 min-h-0 py-4">
              <img
                src={compassSvg}
                alt="Compass"
                className="max-w-[200px] sm:max-w-[280px] md:max-w-[320px] lg:max-w-[400px] h-auto w-full object-contain dark:hidden"
              />
              <img
                src={compassDarkSvg}
                alt="Compass"
                className="max-w-[200px] sm:max-w-[280px] md:max-w-[320px] lg:max-w-[400px] h-auto w-full object-contain hidden dark:block"
              />
            </div>

            {/* Bottom Section: Button and Meta Text */}
            <div className="flex w-full flex-col items-center flex-shrink-0">
              {/* Company Email Message */}
              <p className="text-muted-foreground text-sm font-normal leading-normal pb-4 px-4 text-center font-display">
                Sign in with your{' '}
                <span className="font-semibold">@northbuilt.com</span> Google
                account to access your knowledge base.
              </p>

              {/* Auth Error Display */}
              {authError && (
                <div className="w-full px-4 pb-4" aria-live="polite">
                  <Alert variant="destructive" className="relative pr-10">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{authError}</AlertDescription>
                    <button
                      type="button"
                      onClick={clearAuthError}
                      className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      aria-label="Dismiss error"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </Alert>
                </div>
              )}

              {/* Sign In Button */}
              <div className="flex w-full px-4 py-3">
                <Button
                  onClick={signIn}
                  size="lg"
                  className="w-full h-12 gap-3 text-base font-bold"
                >
                  <svg
                    className="w-[18px] h-[18px] flex-shrink-0"
                    viewBox="0 0 18 18"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g fill="none" fillRule="evenodd">
                      <path
                        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4818h4.8436c-.2086 1.125-.8427 2.0782-1.7772 2.7218v2.2582h2.9087c1.7018-1.5668 2.6836-3.8736 2.6836-6.621z"
                        fill="#4285F4"
                      />
                      <path
                        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2582c-.8059.5405-1.8368.8618-3.0477.8618-2.344 0-4.3282-1.5832-5.036-3.7104H.957v2.3318C2.4382 15.9832 5.4818 18 9 18z"
                        fill="#34A853"
                      />
                      <path
                        d="M3.964 10.71c-.18-.5405-.2822-1.1168-.2822-1.71s.1023-1.1695.2823-1.71V4.9582H.957C.3477 6.1718 0 7.5477 0 9c0 1.4523.3477 2.8282.957 4.0418L3.964 10.71z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.4259 0 9 0 5.4818 0 2.4382 2.0168.957 4.9582L3.964 7.29C4.6718 5.1632 6.656 3.5795 9 3.5795z"
                        fill="#EA4335"
                      />
                    </g>
                  </svg>
                  <span className="truncate">Sign In with Google</span>
                </Button>
              </div>

              {/* Meta Text */}
              <p className="text-muted-foreground text-sm font-normal leading-normal pt-1 px-4 text-center font-display">
                By signing in, you agree to our Terms of Service and Privacy
                Policy.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
